import { useEffect, useState } from "react";
import { Input, ButtonSiguiente } from "../../ui";
import { safeFetch } from "@/api/safeFetch";
import { useNavigate, useParams } from "react-router-dom";
import { Usuario } from "@/types";
import { Role } from "@/utils/rbac";

const EditarUsuarios: React.FC = () => {
    const [busqueda, setBusqueda] = useState("");
    const [usuario, setUsuario] = useState<Usuario | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const controller = new AbortController();
    const signal = controller.signal;
    const navigate = useNavigate();
    const token = localStorage.getItem("token") ?? "";

    const { usuarioId } = useParams<{ usuarioId?: string }>();

    useEffect(() => {
        if (usuarioId) {
            setBusqueda(usuarioId);
            handleBuscar(usuarioId);
        }

        return () => controller.abort();
    }, [usuarioId]);


    // 🔁 Normalizador (igual filosofía que profesionales)
    const normalizeBackendRow = (row: any): Usuario => ({
        usuario_id: row.usuario_id,
        nombre_usuario: row.nombre_usuario,
        nombre: row.nombre,
        apellidos: row.apellidos,
        email: row.email,
        dni: row.dni ?? null,
        nie: row.nie ?? null,

        roles: row.roles ?? [],

        verificado: Boolean(row.verificado),
        creado_en: row.creado_en,
        actualizado_en: row.actualizado_en,

        esAfiliado: Boolean(row.afiliado_id),
        esProfesional: Boolean(row.profesional_id),

        afiliado_id: row.afiliado_id ?? null,
        profesional_id: row.profesional_id ?? null,

        suscripcion: row.suscripcion ?? null,
    });

    // 🔍 Buscar usuario
    const handleBuscar = async (queryParam?: string) => {
        const query = (queryParam ?? busqueda)?.trim();
        if (!query) {
            setError("Introduce DNI / NIE / email o ID");
            return;
        }

        setError("");
        setSuccess("");
        setUsuario(null);

        try {
            const res = await safeFetch(`/api/admin/usuarios/buscar?q=${encodeURIComponent(query)}`, {
                headers: { Authorization: `Bearer ${token}` },
                signal
            });

            // Revisamos el status
            if (!res.ok) {
                const message =
                    res.status === 401
                        ? "No autorizado"
                        : res.status === 404
                            ? "Usuario no encontrado"
                            : `Error ${res.status}`;
                setError(message);
                return;
            }

            // Verificamos que sea JSON
            const contentType = res.headers.get("content-type");
            if (!contentType || !contentType.includes("application/json")) {
                setError("Respuesta del servidor no es JSON");
                console.error("Respuesta no JSON:", await res.text());
                return;
            }

            const data = await res.json();
            const item = Array.isArray(data) ? data[0] : data;

            if (!item) {
                setError("Usuario no encontrado");
                return;
            }

            setUsuario(normalizeBackendRow(item));
        } catch (err: any) {
            if (err.name === "AbortError")
                return; setError(err.message);
        }
    };


    // ✏️ Actualizar usuario
    const handleActualizar = async () => {
        if (!usuario?.usuario_id) return;

        setLoading(true);
        setError("");
        setSuccess("");

        try {
            const res = await safeFetch(
                `/api/editar/${usuario.usuario_id}`,
                {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        nombre_usuario: usuario.nombre_usuario,
                        nombre: usuario.nombre,
                        apellidos: usuario.apellidos,
                        email: usuario.email,
                        dni: usuario.dni,
                        nie: usuario.nie,
                        roles: usuario.roles,
                        esAfiliado: usuario.esAfiliado,
                        esProfesional: usuario.esProfesional,
                        verificado: usuario.verificado,
                    }),
                }
            );


            let data;
            try {
                data = await res.json();
            } catch {
                throw new Error("Respuesta no válida del servidor");
            }

            if (!res.ok) {
                throw new Error(data?.message || "Error al actualizar usuario");
            }

            setSuccess("Usuario actualizado correctamente 🎉");
        } catch (err: any) {
            setError(err?.message ?? "Error de actualización");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        return () => {
            setUsuario(null);
            setBusqueda("");
            setError("");
            setSuccess("");
            setLoading(false);
        };
    }, []);


    return (
        <div className="w-full min-h-screen flex items-center justify-center relative overflow-auto bg-zinc-500 p-4">

            {/* 🔙 Botón volver arriba izquierda */}
            <div className="absolute top-4 left-4">
                <ButtonSiguiente
                    onClick={() => navigate("/backoffice")}
                    className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition shadow"
                >
                    ⬅ Volver
                </ButtonSiguiente>
            </div>

            <div className="w-full max-w-3xl bg-zinc-400 p-6 rounded-xl shadow-md border-zinc-600 border-r-4 border-t-2 flex flex-col gap-6 shadow-purple-700 max-h-[90vh] overflow-y-auto">                <h2 className="text-2xl text-center font-bold">Editar Usuario</h2>

                {/* 🔍 Buscador */}
                <div className="w-full bg-purple-600 p-4 rounded-xl flex gap-4 items-center border-white border-r-4 border-t-2 border shadow-md shadow-purple-700">
                    <Input
                        value={busqueda}
                        onChange={(e) => setBusqueda(e.target.value)}
                        placeholder="DNI / NIE"
                        className="flex-1"
                    />
                    <ButtonSiguiente className="flex-1" onClick={() => handleBuscar()}>
                        Buscar
                    </ButtonSiguiente>
                </div>

                {error && <p className="text-red-600">{error}</p>}
                {success && <p className="text-green-600">{success}</p>}

                {/* ✏️ Formulario */}
                {usuario ? (
                    <div className="flex flex-col gap-4">
                        <div>
                            <label className="font-semibold">Nombre</label>
                            <Input
                                value={usuario.nombre ?? ""}
                                onChange={(e) =>
                                    setUsuario({ ...usuario, nombre: e.target.value })
                                }
                            />
                        </div>

                        <div>
                            <label className="font-semibold">Apellidos</label>
                            <Input
                                value={usuario.apellidos ?? ""}
                                onChange={(e) =>
                                    setUsuario({ ...usuario, apellidos: e.target.value })
                                }
                            />
                        </div>

                        <div>
                            <label className="font-semibold">Email</label>
                            <Input
                                value={usuario.email ?? ""}
                                onChange={(e) =>
                                    setUsuario({ ...usuario, email: e.target.value })
                                }
                            />
                        </div>

                        <div>
                            <label className="font-semibold">DNI</label>
                            <Input
                                value={usuario.dni ?? ""}
                                onChange={(e) =>
                                    setUsuario({ ...usuario, dni: e.target.value })
                                }
                            />
                        </div>

                        <div>
                            <label className="font-semibold">NIE</label>
                            <Input
                                value={usuario.nie ?? ""}
                                onChange={(e) =>
                                    setUsuario({ ...usuario, nie: e.target.value })
                                }
                            />
                        </div>
                        {/* ℹ️ Información del sistema */}

                        <div>
                            <label className="font-semibold">Nombre de usuario</label>
                            <Input
                                value={usuario.nombre_usuario}
                                onChange={(e) =>
                                    setUsuario({ ...usuario, nombre_usuario: e.target.value })
                                }
                            />
                        </div>

                        <div>
                            <label className="font-semibold">Rol</label>
                            <select
                                value={usuario.roles?.[0] ?? ""}
                                onChange={(e) =>
                                    setUsuario({
                                        ...usuario,
                                        roles: [e.target.value as Role]   // ← CASTEO SEGURO
                                    })
                                }
                                className="w-full p-2 "
                            >
                                <option value="usuario">Usuario</option>
                                <option value="profesional">Profesional</option>
                            </select>

                        </div>
                        <div className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                checked={Boolean(usuario.verificado)}
                                onChange={(e) =>
                                    setUsuario({ ...usuario, verificado: e.target.checked })
                                }
                            />
                            <label className="font-semibold">Usuario verificado</label>
                        </div>


                        <div>
                            <label className="font-semibold">Verificado</label>
                            <Input value={usuario.verificado ? "Sí" : "No"} disabled />
                        </div>
                        <div className="grid grid-cols-2 gap-4 bg-zinc-300 p-4 rounded-lg">
                            <div>
                                <label className="font-semibold">Creado el</label>
                                <Input
                                    value={new Date(usuario.creado_en ?? '').toLocaleString()}
                                    disabled
                                />
                            </div>

                            <div className="col-span-2">
                                <label className="font-semibold">Última actualización</label>
                                <Input
                                    value={new Date(usuario.actualizado_en ?? '').toLocaleString()}
                                    disabled
                                />
                            </div>
                        </div>


                        <ButtonSiguiente
                            onClick={handleActualizar}
                            disabled={loading}
                            className="bg-blue-600 text-white py-2 rounded hover:bg-blue-700"
                        >
                            {loading ? "Actualizando..." : "Actualizar Usuario"}
                        </ButtonSiguiente>
                    </div>
                ) : (
                    <p className="text-zinc-600 text-center">
                        Introduce un criterio de búsqueda y pulsa Buscar
                    </p>
                )}
            </div>
        </div>
    );
};

export default EditarUsuarios;
